const SESSION_KEY = 'onalark_session_id';
const CLICK_QUEUE_KEY = 'onalark_click_queue';
const QUEUE_MAX_SIZE = 50;
const QUEUE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

let inMemorySessionId: string | null = null;

export function getSessionId(): string {
  try {
    let sessionId = localStorage.getItem(SESSION_KEY);
    if (!sessionId) {
      sessionId = crypto.randomUUID();
      localStorage.setItem(SESSION_KEY, sessionId);
    }
    return sessionId;
  } catch {
    if (!inMemorySessionId) {
      inMemorySessionId = crypto.randomUUID();
    }
    return inMemorySessionId;
  }
}

interface QueuedClick {
  id: string;
  payload: Record<string, unknown>;
  queuedAt: number;
}

function loadQueue(): QueuedClick[] {
  try {
    const raw = localStorage.getItem(CLICK_QUEUE_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as QueuedClick[];
  } catch {
    return [];
  }
}

function saveQueue(queue: QueuedClick[]): void {
  try {
    localStorage.setItem(CLICK_QUEUE_KEY, JSON.stringify(queue));
  } catch {
  }
}

function enqueue(payload: Record<string, unknown>): string {
  const id = crypto.randomUUID();
  const queue = loadQueue();
  const entry: QueuedClick = { id, payload, queuedAt: Date.now() };
  const trimmed = queue.slice(-(QUEUE_MAX_SIZE - 1));
  saveQueue([...trimmed, entry]);
  return id;
}

function dequeue(id: string): void {
  const queue = loadQueue();
  saveQueue(queue.filter((e) => e.id !== id));
}

async function sendClickPayload(payload: Record<string, unknown>): Promise<boolean> {
  try {
    const res = await fetch('/api/track/click', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    });
    return res.ok || res.status === 204;
  } catch {
    return false;
  }
}

async function sendFeaturedPayload(payload: Record<string, unknown>): Promise<boolean> {
  try {
    const res = await fetch('/api/analytics/featured', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    });
    return res.ok || res.status === 204;
  } catch {
    return false;
  }
}

async function sendFilterUiPayload(payload: Record<string, unknown>): Promise<boolean> {
  try {
    const res = await fetch('/api/analytics/filter-ui', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    });
    return res.ok || res.status === 204;
  } catch {
    return false;
  }
}

export async function drainClickQueue(): Promise<void> {
  const queue = loadQueue();
  if (queue.length === 0) return;

  const now = Date.now();
  const valid = queue.filter((e) => now - e.queuedAt < QUEUE_MAX_AGE_MS);

  if (valid.length < queue.length) {
    saveQueue(valid);
  }

  for (const entry of valid) {
    const success = await sendClickPayload(entry.payload);
    if (success) {
      dequeue(entry.id);
    }
  }
}

export interface SearchFilters {
  categories?: string[];
  excludedCategories?: string[];
  sources?: string[];
  excludedSources?: string[];
  regions?: string[];
  excludedRegions?: string[];
  dateFilter?: string | null;
  customDate?: string | null;
  timePeriods?: string[];
}

export type SearchTriggerType = 'text_input' | 'filter_apply';

function hasActiveFilters(filters: SearchFilters): boolean {
  return !!(
    filters.categories?.length ||
    filters.excludedCategories?.length ||
    filters.sources?.length ||
    filters.excludedSources?.length ||
    filters.regions?.length ||
    filters.excludedRegions?.length ||
    filters.dateFilter ||
    filters.customDate ||
    filters.timePeriods?.length
  );
}

function isNoiseTerm(normalizedTerm: string): boolean {
  return /^[a-zA-Z]{1,2}$/.test(normalizedTerm);
}

const recentSearchKeys = new Set<string>();

function buildSearchDedupeKey(sessionId: string, normalizedTerm: string, filters: SearchFilters, resultsCount: number | undefined, triggerType: SearchTriggerType): string {
  return JSON.stringify({ sessionId, t: normalizedTerm, f: filters, r: resultsCount, trigger: triggerType });
}

export function trackSearch(term: string | null, filters: SearchFilters, resultsCount?: number, triggerType: SearchTriggerType = 'text_input'): void {
  try {
    const normalizedTerm = (term ?? '').trim();
    const filtersActive = hasActiveFilters(filters);

    if (!normalizedTerm && !filtersActive) return;
    if (isNoiseTerm(normalizedTerm) && triggerType === 'text_input') return;

    const sessionId = getSessionId();
    const dedupeKey = buildSearchDedupeKey(sessionId, normalizedTerm, filters, resultsCount, triggerType);
    if (recentSearchKeys.has(dedupeKey)) return;

    recentSearchKeys.add(dedupeKey);
    setTimeout(() => recentSearchKeys.delete(dedupeKey), 5000);

    const payload = JSON.stringify({
      term: normalizedTerm,
      filters,
      sessionId,
      resultsCount,
      triggerType,
    });

    if (navigator.sendBeacon) {
      navigator.sendBeacon(
        '/api/track/search',
        new Blob([payload], { type: 'application/json' })
      );
    } else {
      fetch('/api/track/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
  }
}

export type ClickContext =
  | 'search_results'
  | 'filtered_browse'
  | 'discovery_chip_browse'
  | 'source_view'
  | 'recently_viewed'
  | 'guide_pick'
  | 'direct_unknown';

export type OutboundInteractionSource =
  | 'drawer_cta'
  | 'card_direct'
  | 'source_badge'
  | 'event_detail'
  | 'venue_page'
  | 'featured';

export interface EventClickData {
  title: string;
  venue: string;
  date: string;
  time: string;
  eventDate?: string | null;
  category?: string | null;
  sourceUrl?: string | null;
  source?: string | null;
  region?: string | null;
  eventId?: string | null;
  eventSlug?: string | null;
  interactionSource?: OutboundInteractionSource | null;
  clickContext?: ClickContext | null;
  searchTriggerType?: string | null;
  filtersJson?: Record<string, unknown> | null;
  queryTerm?: string | null;
  discoveryCollapsed?: boolean | null;
  hasDescription?: boolean | null;
  descriptionLengthBucket?: 'none' | 'short' | 'medium' | 'long' | null;
}

export interface TrackedOutboundData extends EventClickData {
  sourceUrl: string;
  interactionSource: OutboundInteractionSource;
}

function appendTrackedParam(params: URLSearchParams, key: string, value: string | null | undefined) {
  if (typeof value !== 'string') return;
  const trimmed = value.trim();
  if (!trimmed) return;
  params.set(key, trimmed);
}

function appendTrackedBooleanParam(params: URLSearchParams, key: string, value: boolean | null | undefined) {
  if (typeof value !== 'boolean') return;
  params.set(key, value ? 'true' : 'false');
}

function serializeFiltersJson(filtersJson: Record<string, unknown> | null | undefined): string | null {
  if (!filtersJson) return null;
  try {
    const serialized = JSON.stringify(filtersJson);
    return serialized.length <= 1500 ? serialized : null;
  } catch {
    return null;
  }
}

export function buildTrackedOutboundUrl(data: TrackedOutboundData): string {
  const params = new URLSearchParams();

  params.set('destination', data.sourceUrl);
  params.set('sessionId', getSessionId());
  params.set('interactionSource', data.interactionSource);

  appendTrackedParam(params, 'title', data.title);
  appendTrackedParam(params, 'venue', data.venue);
  appendTrackedParam(params, 'date', data.date);
  appendTrackedParam(params, 'time', data.time);
  appendTrackedParam(params, 'eventDate', data.eventDate ?? null);
  appendTrackedParam(params, 'category', data.category ?? null);
  appendTrackedParam(params, 'source', data.source ?? null);
  appendTrackedParam(params, 'region', data.region ?? null);
  appendTrackedParam(params, 'eventId', data.eventId ?? null);
  appendTrackedParam(params, 'eventSlug', data.eventSlug ?? null);
  appendTrackedParam(params, 'clickContext', data.clickContext ?? null);
  appendTrackedParam(params, 'searchTriggerType', data.searchTriggerType ?? null);
  appendTrackedParam(params, 'queryTerm', data.queryTerm ?? null);
  appendTrackedBooleanParam(params, 'discoveryCollapsed', data.discoveryCollapsed ?? null);
  appendTrackedBooleanParam(params, 'hasDescription', data.hasDescription ?? null);
  appendTrackedParam(params, 'descriptionLengthBucket', data.descriptionLengthBucket ?? null);

  const serializedFilters = serializeFiltersJson(data.filtersJson ?? null);
  if (serializedFilters) {
    params.set('filtersJson', serializedFilters);
  }

  return `/api/outbound?${params.toString()}`;
}

export function openTrackedOutbound(data: TrackedOutboundData, target: '_blank' | '_self' = '_blank'): string {
  const href = buildTrackedOutboundUrl(data);

  if (target === '_blank') {
    window.open(href, '_blank', 'noopener,noreferrer');
  } else {
    window.location.assign(href);
  }

  return href;
}

export function trackViewSwitch(fromView: string, toView: string): void {
  try {
    const payload = JSON.stringify({
      sessionId: getSessionId(),
      eventType: 'view_switch',
      fromView,
      toView,
    });

    if (navigator.sendBeacon) {
      navigator.sendBeacon(
        '/api/track/view',
        new Blob([payload], { type: 'application/json' })
      );
    } else {
      fetch('/api/track/view', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
  }
}

export function trackSourceSelect(sourceName: string, sourceUrl: string | null): void {
  try {
    const payload = JSON.stringify({
      sessionId: getSessionId(),
      eventType: 'source_select',
      sourceName,
      sourceUrl,
    });

    if (navigator.sendBeacon) {
      navigator.sendBeacon(
        '/api/track/view',
        new Blob([payload], { type: 'application/json' })
      );
    } else {
      fetch('/api/track/view', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
  }
}

export interface DrawerInteractionData {
  eventId?: string | null;
  eventSlug?: string | null;
  eventKey?: string | null;
  title: string;
  venue?: string | null;
  source?: string | null;
  sourceUrl?: string | null;
  category?: string | null;
  region?: string | null;
  clickContext?: ClickContext | null;
  searchTriggerType?: string | null;
  queryTerm?: string | null;
  filtersJson?: Record<string, unknown> | null;
  discoveryCollapsed?: boolean | null;
}

export function trackDrawerInteraction(
  eventType: 'drawer_open' | 'drawer_close' | 'drawer_cta_click' | 'card_expand',
  data: DrawerInteractionData,
): void {
  try {
    const payload = JSON.stringify({
      sessionId: getSessionId(),
      eventType,
      eventId: data.eventId ?? null,
      eventSlug: data.eventSlug ?? null,
      eventKey: data.eventKey ?? null,
      title: data.title,
      venue: data.venue ?? null,
      source: data.source ?? null,
      sourceUrl: data.sourceUrl ?? null,
      category: data.category ?? null,
      region: data.region ?? null,
      clickContext: data.clickContext ?? null,
      searchTriggerType: data.searchTriggerType ?? null,
      queryTerm: data.queryTerm ?? null,
      filtersJson: data.filtersJson ?? null,
      discoveryCollapsed: data.discoveryCollapsed ?? null,
    });

    if (navigator.sendBeacon) {
      navigator.sendBeacon(
        '/api/track/drawer',
        new Blob([payload], { type: 'application/json' })
      );
    } else {
      fetch('/api/track/drawer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
  }
}

export function trackEventClick(data: EventClickData): void {
  try {
    const payload: Record<string, unknown> = {
      ...data,
      sessionId: getSessionId(),
    };

    const queueId = enqueue(payload);

    sendClickPayload(payload).then((success) => {
      if (success) {
        dequeue(queueId);
      }
    });
  } catch (err) {
    console.error('[Track] trackEventClick error:', err);
  }
}

export type FeaturedInteractionType =
  | 'featured_card_click'
  | 'featured_more_details_click'
  | 'featured_carousel_prev'
  | 'featured_carousel_next'
  | 'featured_carousel_dot_select';

export interface FeaturedInteractionData {
  sessionId?: string | null;
  eventType: FeaturedInteractionType;
  eventId?: string | null;
  eventSlug?: string | null;
  title?: string | null;
  venue?: string | null;
  source?: string | null;
  sourceUrl?: string | null;
  category?: string | null;
  region?: string | null;
  position?: number | null;
  selectedIndex?: number | null;
  targetIndex?: number | null;
  featuredCount?: number | null;
  interactionSource?: string | null;
}

export function trackFeaturedInteraction(data: FeaturedInteractionData): void {
  try {
    const payload = {
      ...data,
      sessionId: getSessionId(),
    };

    sendFeaturedPayload(payload).catch(() => {});
  } catch (err) {
    console.error('[Track] trackFeaturedInteraction error:', err);
  }
}

export type FilterUiInteractionType =
  | 'filters_open'
  | 'filter_section_open'
  | 'discovery_panel_expand'
  | 'discovery_panel_collapse'
  | 'summary_chip_open'
  | 'guide_week_click'
  | 'guide_weekend_click';

export type FilterUiSurface =
  | 'header'
  | 'discovery_panel'
  | 'summary_line'
  | 'mobile_summary'
  | 'desktop_summary'
  | 'guide_panel';

export type FilterUiSection = 'regions' | 'categories' | 'sources' | 'date' | 'search' | 'guide';

export interface FilterUiInteractionData {
  sessionId?: string | null;
  interactionType: FilterUiInteractionType;
  surface?: FilterUiSurface | null;
  section?: FilterUiSection | null;
  label?: string | null;
}

export function trackFilterUiInteraction(data: FilterUiInteractionData): void {
  try {
    const payload = {
      ...data,
      sessionId: getSessionId(),
    };

    sendFilterUiPayload(payload).catch(() => {});
  } catch (err) {
    console.error('[Track] trackFilterUiInteraction error:', err);
  }
}

export type GuideButtonType = 'week' | 'weekend';

function getGuideButtonInteractionData(type: GuideButtonType): FilterUiInteractionData {
  return type === 'week'
    ? {
        interactionType: 'guide_week_click',
        surface: 'guide_panel',
        section: 'guide',
        label: "What's On This Week",
      }
    : {
        interactionType: 'guide_weekend_click',
        surface: 'guide_panel',
        section: 'guide',
        label: 'This Weekend',
      };
}

export function trackGuideButtonInteraction(type: GuideButtonType): void {
  trackFilterUiInteraction(getGuideButtonInteractionData(type));
}
